import { View } from "react-native";
import React, { useEffect, useState } from "react";
import MainContainer from "components/MainContainer";
import { SubscriptionDetailScreenProps } from "navigation/types";
import { IndexPath, Select, SelectItem, Text } from "@ui-kitten/components";
import {
  AccountMemberDetails,
  PlanPackageDetails,
  SubscriptionDetails,
} from "features/types";
import { useAppSelector } from "hooks/useAppSelector";
import ProfileAvatar from "components/ProfileAvatar";
import env from "config/env";
import { useAppDispatch } from "hooks/useAppDispatch";
import { fetchAddMemberPlanPackages } from "features/slices/buyFlowSlice";
import MemberSubscriptionList from "./components/MemberSubscriptionList";
import BaseCard from "components/BaseCard";
import { DisplayType } from "./types";
import UpgradePlans from "./views/UpgradePlans";
import Spacer from "components/Spacer";
import { useAccount } from "hooks/useAccount";
import { filterIneligibleSubscriptions } from "utils/subscriptionUtils";
import { getUserName, memberId } from "utils/userUtils";

const findMember = (members: AccountMemberDetails[], id: number) => {
  return members.find((m) => m.id === id);
};

const findSubsByMemberId = (
  subscriptions: SubscriptionDetails[],
  memberId: number
) => {
  return subscriptions.filter(
    (sub) =>
      sub.member &&
      sub.member.id === memberId &&
      filterIneligibleSubscriptions(sub) &&
      !sub.child
  );
};

const SubscriptionDetail = ({
  navigation,
  route,
}: SubscriptionDetailScreenProps) => {
  const [currentMember, setCurrentMember] =
    useState<AccountMemberDetails | null>(null);

  const [currentMemberSubscriptions, setCurrentMemberSubscriptions] = useState<
    SubscriptionDetails[]
  >([]);
  const [selectedSubscription, setSelectedSubscription] =
    useState<SubscriptionDetails | null>(null);
  const [upgradablePlans, setUpgradablePlans] = useState<PlanPackageDetails[]>(
    []
  );
  const [display, setDisplay] = useState<DisplayType>("LIST"); // what should be displayed in current page

  const { planPackages, step } = useAppSelector((state) => state.buyFlow);
  const dispatch = useAppDispatch();
  const memberIdParam = route.params.memberId;
  const { account } = useAccount();

  const goBack = () =>
    setTimeout(() => {
      navigation.navigate("ManageSubscriptionsScreen");
    }, 250);

  const resolveGoBackAction = () => {
    if (display !== "LIST") {
      setDisplay("LIST");
    } else {
      goBack();
    }
  };

  const shouldShowMemberInfo = () => {
    const haveOtherMembers = otherMembers.length > 0;
    return haveOtherMembers && step === 1;
  };

  const otherMembers =
    account?.members.filter((m) => m.id !== currentMember?.id) || [];

  const handleSelect = (memberId: number) => {
    if (!account) return;
    const member = findMember(account.members, memberId);
    setCurrentMember(member!);

    // if user is upgrading their plan and switches user, show them their plans
    // because upgrading needs a pre-selected subscription
    if (display === "UPGRADE") {
      setDisplay("LIST");
    }
  };

  // fetch available plan packages offered by tenant, if already initialized, skip
  useEffect(() => {
    if ((planPackages && planPackages.length > 0) || !account) return;

    dispatch(fetchAddMemberPlanPackages(account));
  }, []);

  // if the member id in url is invalid, go to previous screen
  useEffect(() => {
    if (!account) return;

    const member = findMember(account.members, parseInt(memberIdParam));

    if (member === undefined) {
      goBack();
      return;
    }
    setCurrentMember(member);
  }, [account]);

  // find corresponding member after member changes in drop down selector
  useEffect(() => {
    if (!currentMember) return;
    const memberSubscriptions = findSubsByMemberId(
      account!.subscriptions,
      currentMember.id
    );
    setCurrentMemberSubscriptions(memberSubscriptions);
  }, [currentMember, account]);

  if (currentMember === null) return null;

  return (
    <MainContainer goBackAction={resolveGoBackAction} grayBackground>
      <View style={{ padding: 15 }}>
        {shouldShowMemberInfo() && (
          <>
            <Select
              placeholder="Select different member"
              onSelect={(index) =>
                handleSelect(otherMembers![(index as IndexPath).row].id)
              }
            >
              {otherMembers?.map((member) => (
                <SelectItem
                  title={getUserName(member)}
                  key={member.id}
                ></SelectItem>
              ))}
            </Select>
            <Spacer />

            <BaseCard>
              <View
                style={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                }}
              >
                <View>
                  {currentMember.type === "PRIMARY" && (
                    <Text category="c1">Account Owner</Text>
                  )}
                  <Text category="h6">{getUserName(currentMember)}</Text>
                  <Text>Member ID: {memberId(currentMember.id)}</Text>
                </View>
                <View>
                  <ProfileAvatar
                    source={{
                      uri: `${env.fileUrl}/${currentMember.user.imageIdentifier}`,
                    }}
                    hasImageIdentifier={
                      currentMember.user.imageIdentifier !== null
                    }
                    size={64}
                  ></ProfileAvatar>
                </View>
              </View>
            </BaseCard>
          </>
        )}
        <Spacer />

        {display === "LIST" && (
          <MemberSubscriptionList
            currentMemberSubscriptions={currentMemberSubscriptions}
            setDisplay={setDisplay}
            setSelectedSubscription={setSelectedSubscription}
            setUpgradablePlans={setUpgradablePlans}
            memberId={currentMember.id}
          />
        )}

        {display === "UPGRADE" && (
          <UpgradePlans
            selectedSubscription={selectedSubscription}
            upgradablePlans={upgradablePlans}
          />
        )}
      </View>
    </MainContainer>
  );
};

export default SubscriptionDetail;
